import { cn, insetFocusRing } from './cn'
import { tabIds, useTabHandlers, type TabItem } from './tab-list'
import { useMediaQuery } from './use-media-query'

export interface SectionNavProps {
  readonly sections: readonly TabItem[]
  readonly value: string
  readonly onChange: (id: string) => void
  /** The section list's accessible name. */
  readonly label: string
  /** The small-caps caption above the list, shown at `lg` and up. */
  readonly caption: string
  /** Links each section to the panel given `tabPanelProps(idPrefix, id)`. */
  readonly idPrefix?: string
  readonly className?: string
}

/**
 * The left-hand section switcher for a form with more sections than a tab strip holds. It is
 * tabs, stacked: the active section gets a soft primary fill, primary text and an accent bar on
 * its left edge. Below `lg` the sections wrap in one row.
 */
export function SectionNav({ sections, value, onChange, label, caption, idPrefix, className }: SectionNavProps) {
  // A column from `lg` (1200px), a row below it, so the arrow keys follow the layout.
  const orientation = useMediaQuery('(min-width: 1200px)') ? 'vertical' : 'horizontal'
  const { onClick, onKeyDown } = useTabHandlers(sections, onChange, orientation)

  return (
    <div className={cn('w-full min-w-0 shrink-0 lg:w-64', className)}>
      {/* The tablist's own name already says this to assistive technology. */}
      <p
        aria-hidden="true"
        className="mb-2 hidden px-2 text-xs font-semibold tracking-wider text-text-light uppercase lg:block"
      >
        {caption}
      </p>
      <div
        role="tablist"
        aria-label={label}
        aria-orientation={orientation}
        className="flex flex-row flex-wrap gap-1 border-b border-divider pb-2 lg:sticky lg:top-24 lg:flex-col lg:gap-0.5 lg:border-b-0 lg:pb-0"
      >
        {sections.map((section) => {
          const isActive = section.id === value
          return (
            <button
              key={section.id}
              type="button"
              role="tab"
              {...tabIds(idPrefix, section.id)}
              data-tab-id={section.id}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={onClick}
              onKeyDown={onKeyDown}
              className={cn(
                'relative shrink-0 cursor-pointer rounded-lg px-2.5 py-2 text-left text-sm whitespace-nowrap transition duration-150 lg:whitespace-normal',
                insetFocusRing,
                isActive
                  ? 'bg-primary-soft font-medium text-primary-main before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-full before:bg-accent-main'
                  : 'text-text-primary hover:bg-background-contrast-sm lg:hover:translate-x-0.5',
              )}
            >
              {section.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
